/**
 * Utilitaires du module Ventes (Lot G — front) : conversions monétaires
 * centimes ⇄ euros / HT ⇄ TTC, formatage d'affichage FR, et libellés métier.
 * SOURCE UNIQUE partagée par tous les écrans Ventes (Hub, Catalogue, Clients,
 * Caveau, Assistant B2B, Fiche facture, Paramètres — dédup Phase 8).
 *
 * FRONTIÈRE D'UNITÉS : le back travaille en CENTIMES (entiers) et en POINTS DE
 * BASE pour la TVA (2000 bps = 20 %). Toute conversion vers l'euro affiché/saisi
 * est centralisée ICI (une seule fois) — jamais de « / 100 » éparpillé dans les vues.
 */

import type {
  DocumentStatut,
  DocumentType,
  Marche,
  MoyenReglement,
  RegimeDroits,
  RegimeTva,
  StatutPaiement,
} from "@champ-erp/contracts";
import { htVersTtc } from "@champ-erp/contracts";
import type { BadgeVariant } from "@champ-erp/ui";
// HT → TTC : SOURCE UNIQUE = le moteur pur `vente-calculs.ts` (contrat partagé
// back ⇄ front), ré-exporté ci-dessous. Jamais ré-implémenté (DRY, même arrondi).

// ────────────────────────────────────────────────────────────────────────────
// Conversions & formatage monétaire (centimes ⇄ euros, HT ⇄ TTC)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Montant en centimes → chaîne monétaire FR (« 1 450 » → « 14,50 € », « -1 200 »
 * → « -12,00 € »). Gère les montants SIGNÉS (avoir en négatif). Source unique
 * d'affichage prix.
 */
export function formatEurosCents(cents: number): string {
  return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

/**
 * TTC (centimes) depuis un HT (centimes) + taux de TVA en bps. Ré-export du moteur
 * partagé `vente-calculs.ts` (même arrondi que le back) : aucune divergence possible,
 * exposé ici pour que les écrans importent tout depuis un seul util Ventes.
 */
export { htVersTtc };

/**
 * Parse une saisie euros libre (« 14,50 », « 14.50 », « 1 234,00 ») en CENTIMES
 * entiers. Tolère virgule OU point décimal et les espaces (milliers / insécables).
 * Retourne `null` si la saisie est vide ou invalide (négatif refusé) — l'appelant
 * décide alors du message d'erreur.
 */
export function eurosSaisieVersCents(saisie: string): number | null {
  // \s couvre déjà les espaces insécables Unicode (nbsp, narrow nbsp) des milliers.
  const nettoye = saisie.replace(/\s/g, "").replace(",", ".");
  if (nettoye === "") return null;
  const euros = Number(nettoye);
  if (!Number.isFinite(euros) || euros < 0) return null;
  return Math.round(euros * 100);
}

/**
 * Formate des CENTIMES en valeur décimale de SAISIE (« 1 450 » → « 14,50 »), sans
 * symbole €. Sert à pré-remplir un champ euros en édition (round-trip propre).
 */
export function centsVersEurosSaisie(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

/** Pourcentage saisi (0-100, nombre) → points de base entiers (5 → 500). */
export function pourcentVersBps(pourcent: number): number {
  return Math.round(pourcent * 100);
}

/** Taux de TVA en bps → libellé pourcentage FR (« 2000 » → « 20 % », « 550 » → « 5,5 % »). */
export function formatTauxTvaBps(tauxTvaBps: number): string {
  const pourcent = tauxTvaBps / 100;
  return `${pourcent.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;
}

/**
 * Parse une saisie de taux TVA en pourcentage (« 20 », « 5,5 ») en POINTS DE BASE.
 * Retourne `null` si vide ou invalide (négatif refusé).
 */
export function pourcentSaisieVersBps(saisie: string): number | null {
  const nettoye = saisie.replace(/\s/g, "").replace("%", "").replace(",", ".");
  if (nettoye === "") return null;
  const pourcent = Number(nettoye);
  if (!Number.isFinite(pourcent) || pourcent < 0) return null;
  return Math.round(pourcent * 100);
}

/** Taux de TVA en bps → valeur de SAISIE en pourcentage (« 2000 » → « 20 »). */
export function bpsVersPourcentSaisie(tauxTvaBps: number): string {
  const pourcent = tauxTvaBps / 100;
  return Number.isInteger(pourcent) ? String(pourcent) : String(pourcent).replace(".", ",");
}

// ────────────────────────────────────────────────────────────────────────────
// Libellés métier (aucun anglicisme — vocabulaire imposé cadrage §2)
// ────────────────────────────────────────────────────────────────────────────

/** Libellé FR d'un type de document. */
export const LIBELLE_TYPE_DOCUMENT: Record<DocumentType, string> = {
  devis: "Devis",
  commande: "Commande",
  facture: "Facture",
  avoir: "Avoir",
};

/** Statut d'un document → libellé + variante de badge (jamais la couleur seule). */
export const CONFIG_STATUT_DOCUMENT: Record<
  DocumentStatut,
  { label: string; variant: BadgeVariant }
> = {
  brouillon: { label: "Brouillon", variant: "warning" },
  envoye: { label: "Envoyé", variant: "info" },
  accepte: { label: "Accepté", variant: "success" },
  refuse: { label: "Refusé", variant: "danger" },
  expire: { label: "Expiré", variant: "neutral" },
  a_livrer: { label: "À livrer", variant: "warning" },
  livree: { label: "Livrée", variant: "success" },
  annulee: { label: "Annulée", variant: "neutral" },
  emis: { label: "Émise", variant: "success" },
  annule: { label: "Annulé", variant: "neutral" },
};

/** Marché de destination → libellé FR. */
export const LIBELLE_MARCHE: Record<Marche, string> = {
  france: "France",
  ue: "Union européenne",
  export: "Export (hors UE)",
};

/** Régime de droits d'accise → libellé FR. */
export const LIBELLE_REGIME_DROITS: Record<RegimeDroits, string> = {
  acquittes: "Droits acquittés",
  suspendus: "Droits suspendus",
};

/** Moyen de règlement → libellé FR. */
export const LIBELLE_MOYEN_REGLEMENT: Record<MoyenReglement, string> = {
  especes: "Espèces",
  cheque: "Chèque",
  carte: "Carte bancaire",
  virement: "Virement",
  prelevement: "Prélèvement",
};

/** Statut de paiement dérivé → libellé + variante. */
export const CONFIG_STATUT_PAIEMENT: Record<
  StatutPaiement,
  { label: string; variant: BadgeVariant }
> = {
  impayee: { label: "Impayée", variant: "danger" },
  partiel: { label: "Partiellement réglée", variant: "warning" },
  payee: { label: "Payée", variant: "success" },
  trop_percu: { label: "Trop-perçu", variant: "info" },
};

// TODO Phase 8 : `LIBELLE_REGIME_TVA` est défini mais pas encore branché côté front
// (les mentions TVA du PDF sont posées côté back au snapshot). À câbler sur un récap
// avant émission, ou à retirer si le back reste la seule source des mentions légales.
/** Régime TVA d'une ligne → mention légale affichée. */
export const LIBELLE_REGIME_TVA: Record<RegimeTva, string> = {
  normal: "TVA au taux normal",
  exo_intracom_262ter: "Exonération de TVA, art. 262 ter I du CGI (livraison intra-UE)",
  exo_export_262i: "Exonération de TVA, art. 262 I du CGI (export)",
  franchise_293b: "TVA non applicable, art. 293 B du CGI (franchise en base)",
};

/** Nom lisible d'un client (raison sociale pro, sinon nom + prénom particulier). */
export function nomClient(client: {
  raison_sociale: string | null;
  nom: string | null;
  prenom: string | null;
}): string {
  if (client.raison_sociale) return client.raison_sociale;
  const complet = [client.prenom, client.nom].filter(Boolean).join(" ").trim();
  return complet || "Client sans nom";
}
